import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
} from '@tanstack/react-query';
import { useState } from 'react';
import { sortBy } from 'lodash-es';
import { api } from '@/api';
import { queryClient } from '@/queries';
import {
  Case,
  CaseDuplicateType,
  CaseLogEventType,
  CaseStatus,
  CaseStatusCounts,
  CasesFilters,
  DraftCase,
  OrganType,
  PaginatedBulkAssignmentCasesPayload,
  PaginatedCaseListFilters,
  PaginatedCustomerCaseListFilters,
  ReallocationCandidatesPayload,
  ReviewerType,
} from '@/api/apis/case';
import { PaginatedBody, PaginationSearchParams, SearchQuery, SortOrderQuery, UseQueryConfig } from '@/api/types';
import { StatusGroup, useCasesFiltersStore, useCasesSearchStore } from '@/store/cases-board.store';
import {
  formatFiltersData,
  getCachedCaseListItems,
  getResourcesCombined,
  reconcileCaseAcrossListCaches,
  removeCaseFromAllListCaches,
  shouldRetryOnError,
} from './utils';
import { customerQueries } from '@/queries/customer';
import { employeeQueries } from '@/queries/employee';
import { EmployeeComposed } from '@/api/apis/resources';
import { CustomerMT } from '@/api/apis/customer';
import { useProfileQuery } from '../user';
import { isAllowedStatus } from '@/utils/helpers';
import { MINUTE_IN_MS } from '@/constants/date.constants';
import { activeCaseStatuses } from '@/constants/case/case.constants';
import { caseQueryKeys } from './case.keys';
import { useFormatQueryFields } from '@/hooks/useFormatQueryFields';
import { DataCacheType } from '@/queries/consts';
import { multiSuggestQueryClient } from '@/queries/multi-suggest';

const QUOTE_EVENTS_WITH_TOTALS = [
  CaseLogEventType.CaseQuoteRejectedByTxCenter,
  CaseLogEventType.CaseQuoteApprovedByTxCenter,
  CaseLogEventType.CaseQuoteRecalled,
];

const getCaseResourcesData = async (
  caseId: string,
  type: DataCacheType = DataCacheType.Live,
): Promise<Pick<Case, 'resources'>> => {
  const queryClientInstance = type === DataCacheType.Live ? queryClient : multiSuggestQueryClient;
  const caseData = queryClientInstance.getQueryData(caseQueryKeys.getItem(caseId)) as Case;
  const resources = await getResourcesCombined(caseData.resources);

  return {
    resources,
  };
};

const getCaseReviewsData = async (
  caseId: string,
  type: DataCacheType = DataCacheType.Live,
): Promise<Pick<Case, 'reviews' | 'routeComment'>> => {
  const queryClientInstance = type === DataCacheType.Live ? queryClient : multiSuggestQueryClient;
  const caseData = queryClientInstance.getQueryData(caseQueryKeys.getItem(caseId)) as Case;
  const reviewUsersPromises = await Promise.allSettled<CustomerMT | EmployeeComposed>(
    caseData.reviews.reduce(
      (acc, r) => {
        const reviewerData =
          r.reviewerType === ReviewerType.Customer
            ? queryClientInstance.ensureQueryData(customerQueries.getByUserId(r.reviewerUserId))
            : queryClientInstance.ensureQueryData(employeeQueries.employeeByUserId(r.reviewerUserId));

        return [...acc, reviewerData];
      },
      [] as Promise<CustomerMT | EmployeeComposed>[],
    ),
  );

  const reviewUsers = reviewUsersPromises
    .filter((result) => result.status === 'fulfilled')
    .map((result) => result.value);
  const reviews = caseData.reviews.map((r) => {
    const currentUser = reviewUsers.find((user) => user.userId === r.reviewerUserId);

    return {
      ...r,
      user: currentUser,
    };
  });

  const routeCommentUser = caseData.routeComment?.userId
    ? await queryClient.ensureQueryData(employeeQueries.employeeByUserId(caseData.routeComment.userId))
    : undefined;
  const routeComment = caseData.routeComment
    ? {
        ...caseData.routeComment,
        user: routeCommentUser,
      }
    : null;

  return {
    reviews,
    routeComment,
  };
};

const fetchCaseLogs = async (caseId: string, withQueryTotals = false) => {
  const logs = await api.case.getCaseLogs(caseId);

  if (withQueryTotals) {
    const queryLogs = logs.filter((log) => QUOTE_EVENTS_WITH_TOTALS.includes(log.eventType));

    const queryIds = queryLogs.reduce((acc, log) => {
      const record = log.logRecords.find((r) => !!r.quoteInfo?.isRelatedSumReferable);

      if (record?.quoteInfo?.relatedToQuoteId && !acc.includes(record.quoteInfo.relatedToQuoteId)) {
        return [...acc, record.quoteInfo.relatedToQuoteId];
      }

      return acc;
    }, [] as string[]);

    if (!queryIds.length) return logs;

    const quoteTotals = await api.quote.getQuoteTotals(queryIds);

    return logs.map((log) => {
      if (!QUOTE_EVENTS_WITH_TOTALS.includes(log.eventType)) return log;

      const record = log.logRecords.find((r) => !!r.quoteInfo?.isRelatedSumReferable);

      if (!record?.quoteInfo?.relatedToQuoteId) return log;

      const totals = quoteTotals.totalsByQuoteId[record.quoteInfo.relatedToQuoteId];

      if (!totals) return log;

      return {
        ...log,
        quoteTotals: totals,
      };
    });
  }

  return logs;
};

const updateCaseDataInCaseListCache = (data: Case | DraftCase) => {
  reconcileCaseAcrossListCaches(data as Case);
};
export const caseQueries = {
  infiniteList: (
    {
      enabled,
      ...searchPagination
    }: PaginationSearchParams & UseQueryConfig & SearchQuery & SortOrderQuery & CasesFilters,
    useStaleTime = true,
  ) =>
    infiniteQueryOptions({
      queryKey: caseQueryKeys.getInfiniteList(searchPagination),
      queryFn: ({ pageParam: pageIndex }) => api.case.getCaseList({ ...searchPagination, pageIndex }),
      initialPageParam: 0,
      getNextPageParam: (lastPage) => {
        if (lastPage.pageIndex >= lastPage.totalPages - 1) {
          return undefined;
        }

        return lastPage.pageIndex + 1;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.pageIndex <= 1) {
          return undefined;
        }

        return firstPage.pageIndex - 1;
      },
      enabled,
      ...(useStaleTime ? { placeholderData: keepPreviousData, staleTime: MINUTE_IN_MS * 10 } : { gcTime: 0 }),
    }),
  caseListByIds: (queryParams: Pick<PaginatedCaseListFilters, 'itemsPerPage' | 'caseIds'>) =>
    queryOptions({
      queryKey: caseQueryKeys.getList(queryParams),
      queryFn: () => api.case.getCaseList(queryParams),
      staleTime: MINUTE_IN_MS * 10,
    }),
  counts: (queryParams: Omit<PaginatedCaseListFilters, 'itemsPerPage' | 'pageIndex'>) =>
    queryOptions({
      queryKey: caseQueryKeys.getCounts(queryParams),
      queryFn: () => api.case.getCaseStatusCounts(queryParams),
      staleTime: MINUTE_IN_MS * 10,
    }),
  item: (caseId: string) =>
    queryOptions({
      queryKey: caseQueryKeys.getItem(caseId),
      queryFn: async () => {
        try {
          const caseData: Case | DraftCase = await api.case.getCaseById(caseId);
          updateCaseDataInCaseListCache(caseData);
          return caseData;
        } catch (error) {
          removeCaseFromAllListCaches(caseId);
          throw error;
        }
      },
      retry: shouldRetryOnError,
      staleTime: Infinity,
    }),
  caseReviewData: (caseId: string, type: DataCacheType = DataCacheType.Live) =>
    queryOptions({
      queryKey: caseQueryKeys.getCaseReviewsData(caseId, type),
      queryFn: () => getCaseReviewsData(caseId, type),
    }),
  caseResourcesData: (caseId: string, type: DataCacheType = DataCacheType.Live) =>
    queryOptions({
      queryKey: caseQueryKeys.getCaseResourcesData(caseId, type),
      queryFn: () => getCaseResourcesData(caseId, type),
    }),
  logs: (caseId: string, isFetchQuoteTotals = false) =>
    queryOptions({
      queryKey: caseQueryKeys.getLogs(caseId, isFetchQuoteTotals),
      queryFn: () => fetchCaseLogs(caseId, isFetchQuoteTotals),
    }),
  activityLog: (caseId: string) =>
    queryOptions({
      queryKey: caseQueryKeys.getActivityLogs(caseId),
      queryFn: () => api.case.getCaseActivityLog(caseId),
      retry: shouldRetryOnError,
    }),
  archiveList: (queryParams: PaginatedCaseListFilters | PaginatedCustomerCaseListFilters, useStaleTime = true) =>
    queryOptions({
      queryKey: caseQueryKeys.getArchive(queryParams),
      queryFn: () => api.case.getArchiveCaseList(queryParams),
      placeholderData: keepPreviousData,
      staleTime: useStaleTime ? MINUTE_IN_MS * 10 : 0,
    }),
  accessTickets: (caseId: string) =>
    queryOptions({
      queryKey: caseQueryKeys.getAccessTickets(caseId),
      queryFn: async () => {
        const data = await api.case.accessTickets(caseId);
        return [...data];
      },
      retry: shouldRetryOnError,
    }),
  bulkAssignmentList: (params: PaginatedBulkAssignmentCasesPayload) =>
    queryOptions({
      queryKey: caseQueryKeys.getBulkAssignmentCases(params),
      queryFn: () => api.case.getBulkAssignmentCasesList(params),
      staleTime: MINUTE_IN_MS * 10,
    }),
  reallocationCandidates: (params: ReallocationCandidatesPayload) =>
    queryOptions({
      queryKey: caseQueryKeys.getReallocationCandidates(params),
      queryFn: () => api.case.getReallocationCandidates(params),
      placeholderData: keepPreviousData,
    }),
  getAllDuplicatesList: () =>
    queryOptions<PaginatedBody<CaseDuplicateType>>({
      queryKey: caseQueryKeys.getDuplicates(),
      queryFn: () => api.case.getDuplicatesList(),
      select: (res) => {
        const organOrder = [OrganType.Lungs, OrganType.Liver, OrganType.Heart, OrganType.Kidney];

        return {
          ...res,
          items: sortBy(res.items, [(item) => organOrder.indexOf(item.organType), 'caseName']),
        };
      },
      staleTime: MINUTE_IN_MS,
    }),
};

const useCaseByIdQuery = (id: string = '', enabled: boolean = true) => {
  return useQuery({
    ...caseQueries.item(id),
    staleTime: 60 * 1000,
    enabled: Boolean(id && enabled),
  });
};

export const useCaseResourcesDataQuery = (
  id: string = '',
  useExtendedResourcesInfo = true,
  type: DataCacheType = DataCacheType.Live,
) => {
  return useQuery({
    ...caseQueries.caseResourcesData(id, type),
    enabled: useExtendedResourcesInfo,
  });
};

export const useCaseReviewsDataQuery = (
  id: string = '',
  useExtendedResourcesInfo = true,
  type: DataCacheType = DataCacheType.Live,
) => {
  return useQuery({
    ...caseQueries.caseReviewData(id, type),
    enabled: useExtendedResourcesInfo,
  });
};

const useArchiveCasesListQuery = (
  filters: PaginatedCaseListFilters | PaginatedCustomerCaseListFilters,
  options?: UseQueryConfig,
) => {
  return useQuery({
    ...caseQueries.archiveList(filters),
    ...options,
  });
};

const useReallocationCandidatesQuery = (params: ReallocationCandidatesPayload, options: UseQueryConfig = {}) => {
  return useQuery({
    ...caseQueries.reallocationCandidates(params),
    ...options,
  });
};

const useArchiveCasesListWithFormattedQueryParams = (
  filters: PaginatedCaseListFilters | PaginatedCustomerCaseListFilters,
  options?: UseQueryConfig,
) => {
  const formattedFilters = useFormatQueryFields(
    filters,
    ['trialProgramIds', 'serviceRegionIds', 'randomizationResultFilter', 'txCenterIds', 'opoIds', 'donorHospitalIds'],
    (value: unknown) => (typeof value === 'string' ? value.split(',') : value),
  );
  return useArchiveCasesListQuery(formattedFilters, options);
};

const useInfiniteCustomerCasesListQuery = (
  filters: PaginatedCustomerCaseListFilters,
): UseInfiniteQueryResult<Case[], Error> => {
  return useInfiniteQuery({
    ...caseQueries.infiniteList(
      {
        itemsPerPage: 10,
        pageIndex: 0,
        ...filters,
      },
      false,
    ),
    select: (data): Case[] => data?.pages?.flatMap((page: PaginatedBody<Case>) => page.items) ?? [],
  });
};

export const useCasesBoardFilters = () => {
  const { data: profile } = useProfileQuery();
  const { setFilters, ...filters } = useCasesFiltersStore();
  const searchQuery = useCasesSearchStore((state) => state.searchQuery);

  return formatFiltersData(filters, searchQuery, profile?.employee?.id);
};

export const useCasesBoardFiltersKey = () => {
  const boardFilters = useCasesBoardFilters();
  const duplicates = useCasesFiltersStore((state) => state.duplicates);

  return JSON.stringify({ boardFilters, duplicates });
};

const useCasesBoardFiltersReady = () => {
  const { data: profile } = useProfileQuery();
  const filters = useCasesFiltersStore();
  const boardFilters = useCasesBoardFilters();
  const filtersReady = !!profile?.employee?.id && !!Object.keys(boardFilters).length;

  return { filters, boardFilters, filtersReady };
};

const useCasesByRowInfiniteQuery = ({
  status,
  group,
  enabled = true,
}: {
  status?: CaseStatus;
  group?: StatusGroup;
  enabled?: boolean;
}) => {
  const { filters, boardFilters, filtersReady } = useCasesBoardFiltersReady();
  const { data: duplicateList } = useQuery(caseQueries.getAllDuplicatesList());

  const rowStatuses = status ?? (group?.statuses ?? []).join(',');
  const rowStatusSet = status ? [status] : (group?.statuses ?? []);

  const duplicateCaseIds = filters.duplicates
    ? (duplicateList?.items ?? []).filter((d) => rowStatusSet.includes(d.status)).map((d) => d.caseId)
    : [];

  const filtersKey = useCasesBoardFiltersKey();
  const [mountFiltersKey, setMountFiltersKey] = useState<string | null>(filtersKey);

  if (mountFiltersKey !== null && filtersKey !== mountFiltersKey) {
    setMountFiltersKey(null);
  }

  const haveFiltersChanged = mountFiltersKey === null;

  const {
    data: pagesData,
    isLoading,
    isFetching,
    isFetchedAfterMount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...caseQueries.infiniteList(
      {
        itemsPerPage: 15,
        pageIndex: 0,
        ...boardFilters,
        caseStatuses: rowStatuses,
        ...(filters.duplicates ? { caseIds: duplicateCaseIds.join(',') } : {}),
        orderBy: 'CreatedAt',
        asc: false,
        enabled: filtersReady && enabled && (!filters.duplicates || duplicateCaseIds.length > 0),
      },
      false,
    ),
    staleTime: 0,
    gcTime: MINUTE_IN_MS * 5,
    select: (data) => ({ items: data.pages.flatMap((page) => page.items), totalItems: data.pages[0]?.totalItems ?? 0 }),
  });

  const isConfirmedEmptyDuplicatesRow =
    filters.duplicates && duplicateList !== undefined && duplicateCaseIds.length === 0;
  const isAwaitingFilteredData = haveFiltersChanged && !isFetchedAfterMount;
  const rowData = isAwaitingFilteredData ? undefined : pagesData;
  const data = isConfirmedEmptyDuplicatesRow ? [] : rowData?.items;
  const count = isConfirmedEmptyDuplicatesRow ? 0 : rowData?.totalItems;

  return {
    data,
    count,
    isLoading: isLoading || (isAwaitingFilteredData && isFetching),
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export const getCountByStatusSelector = (status: CaseStatus) => (counts: CaseStatusCounts) => counts[status] ?? 0;

export const getCountByGroupSelector = (group?: StatusGroup) => (counts: CaseStatusCounts) =>
  (group?.statuses ?? []).reduce((sum, s) => sum + (counts[s] ?? 0), 0);

const useDuplicateCaseIds = (enabled: boolean) => {
  const { data: duplicateList, isLoading } = useQuery({ ...caseQueries.getAllDuplicatesList(), enabled });

  if (!enabled || isLoading) return undefined;

  return (duplicateList?.items ?? []).map((d) => d.caseId);
};

const useBoardCountsQueryOptions = () => {
  const { filters, boardFilters, filtersReady } = useCasesBoardFiltersReady();
  const duplicateCaseIds = useDuplicateCaseIds(filters.duplicates);

  return {
    params: {
      ...boardFilters,
      ...(filters.duplicates ? { caseIds: (duplicateCaseIds ?? []).join(',') } : {}),
    },
    enabled: filtersReady && (!filters.duplicates || !!duplicateCaseIds?.length),
    confirmedNoDuplicates: filters.duplicates && duplicateCaseIds?.length === 0,
  };
};

const useCaseStatusCountForRow = ({ status, group }: { status?: CaseStatus; group?: StatusGroup }) => {
  const { params, enabled, confirmedNoDuplicates } = useBoardCountsQueryOptions();

  const countsQuery = useQuery({
    ...caseQueries.counts(params),
    select: status ? getCountByStatusSelector(status) : getCountByGroupSelector(group),
    enabled,
  });

  return confirmedNoDuplicates ? 0 : countsQuery.data;
};

export const useCasesBoardActiveCountQuery = () => {
  const { params, enabled } = useBoardCountsQueryOptions();

  return useQuery({
    ...caseQueries.counts(params),
    select: (counts) => activeCaseStatuses.reduce((sum, s) => sum + (counts[s] ?? 0), 0),
    placeholderData: keepPreviousData,
    enabled,
  });
};

const useCaseLogsQuery = (caseId: string, isFetchQuoteTotals = false) => {
  return useQuery(caseQueries.logs(caseId, isFetchQuoteTotals));
};

const useCaseActivityLogQuery = (caseId: string, enabled = true) => {
  return useQuery({
    ...caseQueries.activityLog(caseId),
    enabled: Boolean(enabled),
  });
};

const useDuplicatesForCaseQuery = ({
  donorId,
  organType,
  excludeCaseId,
  caseStatus,
}: {
  donorId: string;
  organType: OrganType;
  excludeCaseId?: string;
  caseStatus?: CaseStatus;
}) => {
  const shouldCheckForDuplicates =
    !!caseStatus &&
    isAllowedStatus({
      statusFrom: CaseStatus.Draft,
      statusTo: CaseStatus.Accepted,
      currentStatus: caseStatus,
    });

  const enabled = shouldCheckForDuplicates && !!donorId && !!organType;
  const caseList = enabled ? getCachedCaseListItems() : [];

  const duplicates = caseList.filter(
    (c) =>
      c.donorId?.toUpperCase() === donorId.toUpperCase() &&
      c.organType === organType &&
      c.caseId !== excludeCaseId &&
      c.status !== CaseStatus.Closed,
  );

  return { data: { items: duplicates } };
};

const useAllDuplicatesListQuery = ({ enabled = true }: { enabled?: boolean }) => {
  return useQuery({ ...caseQueries.getAllDuplicatesList(), enabled });
};

const useBulkAssignmentListQuery = (params: PaginatedBulkAssignmentCasesPayload & SortOrderQuery) => {
  return useQuery(caseQueries.bulkAssignmentList(params));
};

type BulkAssignmentStatusQueryOptions = {
  refetchInterval?: number | false;
};

const useBulkAssignmentStatusQuery = ({ refetchInterval = false }: BulkAssignmentStatusQueryOptions = {}) => {
  return useQuery({
    queryKey: caseQueryKeys.getBulkAssignmentStatus(),
    queryFn: () => api.case.getBulkAssignmentStatus(),
    staleTime: MINUTE_IN_MS / 6,
    gcTime: MINUTE_IN_MS,
    retry: false,
    refetchInterval,
    refetchIntervalInBackground: false,
  });
};

const useCaseAccessTicketsQuery = (caseId: string, enabled = true) => {
  return useQuery({
    ...caseQueries.accessTickets(caseId),
    enabled: Boolean(caseId && enabled),
  });
};

export {
  useCaseByIdQuery,
  useCaseLogsQuery,
  useCaseActivityLogQuery,
  useInfiniteCustomerCasesListQuery,
  useArchiveCasesListQuery,
  useReallocationCandidatesQuery,
  useArchiveCasesListWithFormattedQueryParams,
  useCasesByRowInfiniteQuery,
  useCaseStatusCountForRow,
  useDuplicateCaseIds,
  useBoardCountsQueryOptions,
  useDuplicatesForCaseQuery,
  useAllDuplicatesListQuery,
  useBulkAssignmentListQuery,
  useBulkAssignmentStatusQuery,
  useCaseAccessTicketsQuery,
};
